import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Spinner, Alert, Row, Col } from 'react-bootstrap';
import accountLockoutService from '../../services/accountLockoutService';
import { sanitizeInput, validateNumericInput } from '../../utils/xssProtection';

/**
 * 로그인 시도 횟수 설정을 관리하는 컴포넌트
 */
const LoginAttemptSettings = () => {
  const [settings, setSettings] = useState({
    max_attempts: { value: 5, description: '' },
    lockout_duration: { value: 30, description: '' }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    maxAttempts: 5,
    lockoutDuration: 30
  });
  const [validationErrors, setValidationErrors] = useState({});
  
  // 초기 설정값 로드
  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountLockoutService.getLoginAttemptSettings();
      setSettings(data);
      setForm({
        maxAttempts: data.max_attempts?.value || 5,
        lockoutDuration: data.lockout_duration?.value || 30
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || '설정을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // 컴포넌트 마운트 시 설정 로드
  useEffect(() => {
    loadSettings();
  }, []);
  
  // 폼 입력값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    const numericValue = parseInt(sanitizedValue, 10) || 0;
    
    // 실시간 검증
    let validation = { isValid: true, error: null };
    if (name === 'maxAttempts') {
      validation = validateNumericInput(numericValue, 1, 10);
    } else if (name === 'lockoutDuration') {
      validation = validateNumericInput(numericValue, 5, 1440);
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [name]: validation.isValid ? null : validation.error
    }));
    
    setForm({
      ...form,
      [name]: numericValue
    });
  };
  
  // 설정 저장
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // 입력값 검증
      if (validationErrors.maxAttempts || validationErrors.lockoutDuration) {
        throw new Error('입력값을 확인해주세요.');
      }
      
      if (form.maxAttempts < 1 || form.maxAttempts > 10) {
        throw new Error('최대 로그인 시도 횟수는 1~10 사이의 값이어야 합니다.');
      }
      
      if (form.lockoutDuration < 5 || form.lockoutDuration > 1440) {
        throw new Error('계정 잠금 지속 시간은 5~1440분(24시간) 사이의 값이어야 합니다.');
      }
      
      // API 호출
      await accountLockoutService.updateLoginAttemptSettings(
        form.maxAttempts,
        form.lockoutDuration
      );
      
      setSuccess('설정이 성공적으로 저장되었습니다.');
      loadSettings(); // 설정 다시 로드
    } catch (err) {
      setError(`설정 저장 실패: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">로딩 중...</span>
        </Spinner>
      </div>
    );
  }
  
  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">로그인 시도 제한 설정</h5>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>최대 로그인 시도 횟수</Form.Label>
                <Form.Control
                  type="number"
                  name="maxAttempts"
                  value={form.maxAttempts}
                  onChange={handleChange}
                  min="1"
                  max="10"
                  required
                  isInvalid={!!validationErrors.maxAttempts}
                />
                {validationErrors.maxAttempts && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.maxAttempts}
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-muted">
                  {settings.login_max_attempts?.description || 
                    '연속 로그인 실패 시 계정이 잠기는 시도 횟수 (1~10회)'}
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>계정 잠금 지속 시간 (분)</Form.Label>
                <Form.Control
                  type="number"
                  name="lockoutDuration"
                  value={form.lockoutDuration}
                  onChange={handleChange}
                  min="5"
                  max="1440"
                  required
                  isInvalid={!!validationErrors.lockoutDuration}
                />
                {validationErrors.lockoutDuration && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.lockoutDuration}
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-muted">
                  {settings.login_lockout_duration?.description || 
                    '계정 잠금 후 자동으로 잠금이 해제되는 시간 (5~1440분)'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-flex justify-content-end">
            <Button 
              type="submit" 
              variant="primary" 
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner 
                    as="span" 
                    animation="border" 
                    size="sm" 
                    role="status" 
                    aria-hidden="true" 
                  />
                  <span className="ms-2">저장 중...</span>
                </>
              ) : '설정 저장'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default LoginAttemptSettings;
